import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isAxiosError } from "axios";
import { loginRequest } from "../../api/authApi";
import { useAuthStore } from "../../store/authStore";
import { Spinner } from "../UI/Spinner";

interface LocationState {
  from?: { pathname?: string };
}

export function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState)?.from?.pathname ?? "/plans";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await loginRequest(email.trim().toLowerCase(), password);
      setAuth(res.accessToken, res.user);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const message = isAxiosError(err)
        ? (err.response?.data as { error?: { message?: string } } | undefined)
            ?.error?.message ?? "Credenciales invalidas"
        : "Error inesperado";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mx-auto flex w-full max-w-sm flex-col gap-4 rounded-xl bg-slate-800/70 p-6 shadow-xl ring-1 ring-slate-700"
    >
      <h1 className="text-2xl font-semibold text-white">IndustrialMap</h1>
      <p className="text-sm text-slate-400">Inicia sesion para continuar.</p>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-slate-300">Email</span>
        <input
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-white placeholder-slate-500 focus:border-brand-light focus:outline-none"
          placeholder="usuario@empresa.com"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-slate-300">Contrasena</span>
        <input
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-white focus:border-brand-light focus:outline-none"
        />
      </label>

      {error && (
        <p
          role="alert"
          className="rounded-md border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm text-red-300"
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 font-medium text-white transition hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting && <Spinner size={16} />}
        {submitting ? "Ingresando..." : "Ingresar"}
      </button>
    </form>
  );
}
